import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import API from "../api";
import { socket } from "../socket";

const AuthContext = createContext(null);

/**
 * Normalize the raw API user response into a consistent shape
 * used throughout the app.
 */
function normalizeUser(data) {
  return {
    id: data.id || data._id,
    name: data.name,
    email: data.email,
    role: data.role,
    specialization: data.specialization || "",
    education: data.education || "",
    experience: data.experience ?? "",
    clinic: data.clinic || "",
    city: data.city || "",
    fee: data.fee ?? "",
    gender: data.gender || "",
    phone: data.phone || "",
    licenseNumber: data.licenseNumber || "",
    about: data.about || "",
    dateOfBirth: data.dateOfBirth || "",
    bloodGroup: data.bloodGroup || "",
    address: data.address || "",
    emergencyContactName: data.emergencyContactName || "",
    emergencyContactPhone: data.emergencyContactPhone || "",
    pharmacyName: data.pharmacyName || "",
    ownerName: data.ownerName || "",
    companyName: data.companyName || "",
    department: data.department || "",
    status: data.status || null,
    rejectionReason: data.rejectionReason || "",
    emailVerified: data.emailVerified,
    approvalStatus: data.approvalStatus || "not_required",
    profileCompleted: !!data.profileCompleted,
    profileCompletion: data.profileCompletion || {},
    availabilityConfigured: data.availabilityConfigured || false,
    availability: data.availability || { slotDurationMin: 30, weekly: [] },
    profilePic: data.profilePic || "",
    insurancePlan: data.insurancePlan || "none",
    insuranceStatus: data.insuranceStatus || "inactive",
  };
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wrapped setUser that also persists to localStorage
  const setUser = useCallback((userData) => {
    if (userData) {
      const normalized = userData.id ? userData : normalizeUser(userData);
      setUserState(normalized);
      localStorage.setItem("user", JSON.stringify(normalized));
    } else {
      setUserState(null);
      localStorage.removeItem("user");
    }
  }, []);

  // Login: the backend has already set the httpOnly auth cookie on the login
  // response, so there is no token to store client-side. Supports both the
  // legacy login(token, userData) and the cookie-era login(userData) signatures.
  const login = useCallback((arg1, arg2) => {
    const userData = arg2 !== undefined ? arg2 : arg1;
    const normalized = normalizeUser(userData);
    setUser(normalized);

    if (!socket.connected) socket.connect();
    socket.emit("join", { userId: normalized.id });
  }, [setUser]);

  // Logout: clear the server-side cookie, then drop local state + socket.
  const logout = useCallback(async () => {
    try {
      await API.post("/auth/logout");
    } catch {
      // best-effort: even if the call fails, clear client state below
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUserState(null);

    if (socket.connected) socket.disconnect();
  }, []);

  // Bootstrap: auth lives in an httpOnly cookie the client can't read, so always
  // ask the server who we are — the cookie rides along automatically.
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    document.documentElement.dataset.theme = savedTheme;

    API.get("/auth/me")
      .then((res) => {
        const normalized = normalizeUser(res.data);
        setUser(normalized);

        if (!socket.connected) socket.connect();
        socket.emit("join", { userId: normalized.id });
      })
      .catch(() => {
        localStorage.removeItem("user");
        setUserState(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const value = { user, setUser, login, logout, loading };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context.
 * Usage: const { user, setUser, login, logout, loading } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
